interface NotFoundErrorLike {
  status?: unknown;
  statusCode?: unknown;
  response?: {
    status?: unknown;
  };
  name?: unknown;
  message?: unknown;
}

/**
 * Node's DNS errno, as in `getaddrinfo ENOTFOUND api.medplum.com`. It spells the "notfound" token
 * but means the host name did not resolve — transient, and never the 404 a caller acts on. Matched
 * on word boundaries so a genuine `ResourceNotFoundException` is untouched.
 */
const DNS_ERRNO = /\benotfound\b/;

function includesNotFoundToken(value: string): boolean {
  if (DNS_ERRNO.test(value)) return false;
  return value.includes("not found") || value.includes("notfound") || value.includes("not-found");
}

function getStatusCode(error: NotFoundErrorLike): number | undefined {
  if (typeof error.status === "number") {
    return error.status;
  }

  if (typeof error.statusCode === "number") {
    return error.statusCode;
  }

  return typeof error.response?.status === "number" ? error.response.status : undefined;
}

/**
 * Detects "not found" errors across API/client error shapes.
 */
export function isNotFoundError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const maybeError = error as NotFoundErrorLike;
  const status = getStatusCode(maybeError);
  const name = typeof maybeError.name === "string" ? maybeError.name.toLowerCase() : "";
  const message = typeof maybeError.message === "string" ? maybeError.message.toLowerCase() : "";

  if (status === 404) {
    return true;
  }

  return includesNotFoundToken(name) || includesNotFoundToken(message);
}
